import { supabase, formatSupabaseError } from '../../supabase';
import { stampCreatedAt } from '../../utils/dbTimestamps';

const SETTINGS_ID = 'default';
const GATEWAY_ID = 'default';
const NOTIFICATIONS_ID = 'default';

export async function fetchPlatformSettings() {
  const { data, error } = await supabase
    .from('platform_settings')
    .select('*')
    .eq('id', SETTINGS_ID)
    .maybeSingle();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load settings.'));
  }

  return {
    softwareName: data?.software_name || 'Gym CRM',
    currency: {
      code: data?.currency_code || 'PKR',
      symbol: data?.currency_symbol || 'Rs',
      position: data?.currency_position || 'before',
      decimalPlaces: Number(data?.decimal_places ?? 2),
    },
  };
}

export async function savePlatformSettings(settings) {
  const row = {
    id: SETTINGS_ID,
    software_name: settings.softwareName?.trim() || 'Gym CRM',
    currency_code: settings.currency?.code || 'PKR',
    currency_symbol: settings.currency?.symbol || 'Rs',
    currency_position: settings.currency?.position || 'before',
    decimal_places: Number(settings.currency?.decimalPlaces ?? 2),
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('platform_settings').upsert(row);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not save settings.'));
  }
  return settings;
}

export async function fetchPaymentGateway() {
  const { data, error } = await supabase
    .from('platform_payment_gateway')
    .select('*')
    .eq('id', GATEWAY_ID)
    .maybeSingle();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load payment gateway.'));
  }

  return {
    methods: data?.enabled_methods || {},
    apiKeys: data?.api_keys || {},
    apiConnections: data?.api_connections || {},
    bankAccount: data?.bank_account || {},
    merchantInfo: data?.merchant_info || {},
  };
}

export async function savePaymentGateway(payload) {
  const row = {
    id: GATEWAY_ID,
    enabled_methods: payload.methods || {},
    api_keys: payload.apiKeys || {},
    api_connections: payload.apiConnections || {},
    bank_account: payload.bankAccount || {},
    merchant_info: payload.merchantInfo || {},
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('platform_payment_gateway').upsert(row);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not save payment gateway.'));
  }
}

export async function fetchNotificationSettings() {
  const { data, error } = await supabase
    .from('platform_notification_settings')
    .select('*')
    .eq('id', NOTIFICATIONS_ID)
    .maybeSingle();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load notification settings.'));
  }

  const rulesPayload = data?.reminder_rules;
  const isMeta = rulesPayload && !Array.isArray(rulesPayload) && typeof rulesPayload === 'object';
  const rules = Array.isArray(rulesPayload)
    ? rulesPayload
    : (Array.isArray(rulesPayload?.rules) ? rulesPayload.rules : []);

  return {
    globalConfig: {
      emailConnected: Boolean(data?.email_connected),
      emailEnabled: Boolean(data?.email_enabled),
      emailSource: data?.email_source || '',
      defaultSendTime: data?.default_send_time || '09:00',
      adminEmail: data?.admin_email || '',
      sendInvoiceCopyToOwner: Boolean(data?.send_invoice_copy_to_owner),
      autoSendOnSave: Boolean(data?.auto_send_on_save),
      smtp: data?.smtp || {},
      whatsappConnected: Boolean(isMeta ? rulesPayload.whatsappConnected : false),
      whatsappEnabled: Boolean(isMeta ? rulesPayload.whatsappEnabled : false),
      whatsappPhone: (isMeta && rulesPayload.whatsappPhone) || '',
      whatsapp: (isMeta && rulesPayload.whatsapp) || {},
    },
    gymReminders: rules,
  };
}

export async function saveNotificationSettings(globalConfig, gymReminders) {
  const row = {
    id: NOTIFICATIONS_ID,
    email_connected: Boolean(globalConfig.emailConnected),
    email_enabled: Boolean(globalConfig.emailEnabled),
    email_source: globalConfig.emailSource || null,
    default_send_time: globalConfig.defaultSendTime || '09:00',
    admin_email: globalConfig.adminEmail || null,
    send_invoice_copy_to_owner: Boolean(globalConfig.sendInvoiceCopyToOwner),
    auto_send_on_save: Boolean(globalConfig.autoSendOnSave),
    smtp: globalConfig.smtp || {},
    // Pack WhatsApp + rules into reminder_rules (no schema migration needed)
    reminder_rules: {
      version: 2,
      rules: Array.isArray(gymReminders) ? gymReminders : [],
      whatsappConnected: Boolean(globalConfig.whatsappConnected),
      whatsappEnabled: Boolean(globalConfig.whatsappEnabled),
      whatsappPhone: globalConfig.whatsappPhone || '',
      whatsapp: globalConfig.whatsapp || {},
    },
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('platform_notification_settings').upsert(row);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not save notification settings.'));
  }
}

export async function fetchNotificationHistory() {
  const { data, error } = await supabase
    .from('platform_notification_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(200);

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load notification history.'));
  }

  return (data || []).map((row) => ({
    id: row.id,
    recipient: row.recipient,
    member: row.gym_name || '—',
    type: row.notification_type,
    typeLabel: row.notification_type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '—',
    channel: row.channel || 'email',
    status: row.status || 'pending',
    sentAt: row.sent_at ? String(row.sent_at).replace('T', ' ').slice(0, 16) : '—',
    message: row.message || '',
  }));
}

/**
 * Opens a pre-filled Gmail compose window (no Supabase Edge Function deploy).
 * User clicks Send in Gmail while logged into the sending account.
 */
export function openManualEmailCompose({ to, fromEmail, subject, message }) {
  const recipient = String(to || '').trim();
  if (!recipient) {
    throw new Error('Enter the receiver email address.');
  }

  const bodyText = String(message || '').trim();
  if (!bodyText) {
    throw new Error('Enter a message to send.');
  }

  const su = encodeURIComponent(String(subject || 'Message from Gym CRM').trim() || 'Message from Gym CRM');
  const body = encodeURIComponent(
    `${bodyText}\n\n`
    + `---`
    + `\nSent via Gym CRM Manual Send`
    + `\nFrom setting in app: ${fromEmail || '—'}`,
  );

  const gmailUrl =
    `https://mail.google.com/mail/?view=cm&fs=1`
    + `&to=${encodeURIComponent(recipient)}`
    + `&su=${su}`
    + `&body=${body}`;

  const popup = window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  if (!popup) {
    window.location.href = `mailto:${encodeURIComponent(recipient)}?subject=${su}&body=${body}`;
  }

  return { ok: true, recipient };
}

/**
 * Simple Gmail check — opens a pre-filled Gmail compose (no Edge Function).
 */
export function openGmailTestCompose({ to, fromEmail }) {
  return openManualEmailCompose({
    to: to || fromEmail,
    fromEmail,
    subject: 'Gym CRM — test email',
    message:
      `This is a test from Gym CRM Owner → Notifications.\n\n`
      + `If you can send this and see it in Sent / the recipient inbox, Gmail is working.\n\n`
      + `Tip: Connect Email should use this same Gmail + a Google App Password.`,
  });
}

export async function logPlatformNotification({
  recipient,
  gymName = 'Platform',
  type = 'test_email',
  channel = 'email',
  status = 'sent',
  message = '',
}) {
  const row = {
    recipient,
    gym_name: gymName,
    notification_type: type,
    channel,
    status,
    message,
    sent_at: status === 'sent' ? new Date().toISOString() : null,
  };
  const { data, error } = await supabase
    .from('platform_notification_logs')
    .insert(stampCreatedAt(row))
    .select('*')
    .maybeSingle();
  if (error) {
    console.warn('Could not write notification log', error);
    return null;
  }
  return data;
}
